import React, { useState, useEffect } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import { Cog6ToothIcon, ClockIcon, CheckCircleIcon, XMarkIcon, CurrencyDollarIcon, DocumentTextIcon } from '@heroicons/react/24/outline'

function ContractSettingsPage() {
  const [activeTab, setActiveTab] = useState('valores')
  const [settings, setSettings] = useState({
    // Aba 1 - Valores e Taxas
    taxa_administrativa_percentual: 15,
    percentual_multa_rescisao: 10,
    franquia_km_mensal: 10000,
    valor_km_excedente: 2.50,
    percentual_multa_atraso: 10,
    percentual_juros_diario: 0.0333,
    percentual_reducao_tda: 0,
    percentual_multa_violacao_rastreamento: 50,
    boleto_protesto_automatico: false,
    boleto_dias_protesto: 3,
    deposit_percentage_default: 10,
    percentual_tda: 30,
    percentual_honorarios_advocaticios: 20,
    // Aba 2 - Prazos e Regras
    prazo_retirada_veiculo: 5,
    prazo_emissao_nf: 5,
    prazo_negativacao: 30,
    prazo_envio_documentos: 7,
    prazo_negociacao_reequilibrio: 30,
    dias_suspensao_servico: 15,
    indice_reajuste_preferencial: 'MAIOR_INDICE',
    // DPO
    dpo_name: '',
    dpo_email: '',
    dpo_phone: ''
  })
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [originalSettings, setOriginalSettings] = useState(null)

  useEffect(() => {
    loadSettings()
    loadHistory()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/contract-settings')
      const result = response.data

      if (result.data) {
        setSettings(result.data)
        setOriginalSettings(result.data)
      }
    } catch (error) {
      console.error('Erro ao carregar configuracoes:', error)
      toast.error('Erro ao carregar configuracoes de contrato')
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    try {
      const response = await api.get('/api/contract-settings/history')
      const result = response.data

      if (result.data) {
        setHistory(result.data)
      }
    } catch (error) {
      console.error('Erro ao carregar historico:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setHasChanges(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validacoes
    if (settings.taxa_administrativa_percentual < 0 || settings.taxa_administrativa_percentual > 100) {
      toast.error('Taxa administrativa deve estar entre 0 e 100%')
      return
    }

    if (settings.percentual_tda < 0 || settings.percentual_tda > 100) {
      toast.error('Percentual TDA deve estar entre 0 e 100%')
      return
    }

    if (settings.prazo_negativacao < 1 || settings.prazo_negativacao > 90) {
      toast.error('Prazo para negativacao deve estar entre 1 e 90 dias')
      return
    }

    try {
      setSaving(true)
      const response = await api.put('/api/contract-settings', {
        taxa_administrativa_percentual: parseFloat(settings.taxa_administrativa_percentual),
        percentual_multa_rescisao: parseFloat(settings.percentual_multa_rescisao),
        franquia_km_mensal: parseInt(settings.franquia_km_mensal),
        valor_km_excedente: parseFloat(settings.valor_km_excedente),
        percentual_multa_atraso: parseFloat(settings.percentual_multa_atraso),
        percentual_juros_diario: parseFloat(settings.percentual_juros_diario),
        percentual_reducao_tda: parseFloat(settings.percentual_reducao_tda),
        percentual_multa_violacao_rastreamento: parseFloat(settings.percentual_multa_violacao_rastreamento),
        boleto_protesto_automatico: settings.boleto_protesto_automatico,
        boleto_dias_protesto: parseInt(settings.boleto_dias_protesto),
        deposit_percentage_default: parseFloat(settings.deposit_percentage_default),
        percentual_tda: parseFloat(settings.percentual_tda),
        percentual_honorarios_advocaticios: parseFloat(settings.percentual_honorarios_advocaticios),
        prazo_retirada_veiculo: parseInt(settings.prazo_retirada_veiculo),
        prazo_emissao_nf: parseInt(settings.prazo_emissao_nf),
        prazo_negativacao: parseInt(settings.prazo_negativacao),
        prazo_envio_documentos: parseInt(settings.prazo_envio_documentos),
        prazo_negociacao_reequilibrio: parseInt(settings.prazo_negociacao_reequilibrio),
        dias_suspensao_servico: parseInt(settings.dias_suspensao_servico),
        indice_reajuste_preferencial: settings.indice_reajuste_preferencial,
        dpo_name: settings.dpo_name,
        dpo_email: settings.dpo_email,
        dpo_phone: settings.dpo_phone
      })

      if (response.data) {
        toast.success('Configuracoes atualizadas com sucesso!')
        setSettings(response.data.data)
        setOriginalSettings(response.data.data)
        setHasChanges(false)
        loadHistory()
      }
    } catch (error) {
      console.error('Erro ao salvar configuracoes:', error)
      toast.error(error.response?.data?.error || 'Erro ao salvar configuracoes')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (originalSettings) {
      setSettings(originalSettings)
      setHasChanges(false)
      toast.success('Alteracoes descartadas')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const tabs = [
    { id: 'valores', name: 'Valores e Taxas', icon: CurrencyDollarIcon },
    { id: 'prazos', name: 'Prazos e Regras', icon: DocumentTextIcon }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Cog6ToothIcon className="w-8 h-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Configuracoes de Contrato
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Configure os valores padrao que serao utilizados na geracao de novos contratos
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <form onSubmit={handleSubmit}>
          {/* Aba 1 - Valores e Taxas */}
          {activeTab === 'valores' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Taxa Administrativa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Taxa Administrativa - TCA (%)
                </label>
                <input
                  type="number"
                  name="taxa_administrativa_percentual"
                  value={settings.taxa_administrativa_percentual}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Taxa de Custos Administrativos sobre despesas reembolsadas
                </p>
              </div>

              {/* Taxa Devolucao Antecipada - TDA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Taxa Devolucao Antecipada - TDA (%)
                </label>
                <input
                  type="number"
                  name="percentual_tda"
                  value={settings.percentual_tda}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Multa compensatoria por rescisao antecipada sobre valores vincendos
                </p>
              </div>

              {/* Multa por Rescisao */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Multa por Rescisao (%)
                </label>
                <input
                  type="number"
                  name="percentual_multa_rescisao"
                  value={settings.percentual_multa_rescisao}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Percentual aplicado em caso de rescisao contratual
                </p>
              </div>

              {/* Franquia KM Mensal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Franquia de KM Mensal
                </label>
                <input
                  type="number"
                  name="franquia_km_mensal"
                  value={settings.franquia_km_mensal}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Quilometragem mensal incluida no contrato
                </p>
              </div>

              {/* Valor KM Excedente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor por KM Excedente (R$)
                </label>
                <input
                  type="number"
                  name="valor_km_excedente"
                  value={settings.valor_km_excedente}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Valor cobrado por cada km alem da franquia
                </p>
              </div>

              {/* Percentual Multa por Atraso */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Multa por Atraso (%)
                </label>
                <input
                  type="number"
                  name="percentual_multa_atraso"
                  value={settings.percentual_multa_atraso}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Multa aplicada sobre parcelas em atraso
                </p>
              </div>

              {/* Percentual Juros Diario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Juros Diario (%)
                </label>
                <input
                  type="number"
                  name="percentual_juros_diario"
                  value={settings.percentual_juros_diario}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.0001"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Juros aplicados por dia de atraso (0.0333% = 1% ao mes)
                </p>
              </div>

              {/* Caucao Padrao */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Caucao Padrao (% sobre valor bruto)
                </label>
                <input
                  type="number"
                  name="deposit_percentage_default"
                  value={settings.deposit_percentage_default}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Percentual padrao de caucao sobre o valor bruto
                </p>
              </div>

              {/* Multa Violacao Rastreamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Multa por Violacao de Rastreamento (%)
                </label>
                <input
                  type="number"
                  name="percentual_multa_violacao_rastreamento"
                  value={settings.percentual_multa_violacao_rastreamento}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Percentual sobre valor liquido mensal em caso de violacao
                </p>
              </div>

              {/* Honorarios Advocaticios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Honorarios Advocaticios (%)
                </label>
                <input
                  type="number"
                  name="percentual_honorarios_advocaticios"
                  value={settings.percentual_honorarios_advocaticios}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Valor fixo em 20% para cobranca judicial (nao editavel)
                </p>
              </div>

              {/* Protesto Automatico */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Protesto Automatico de Boleto
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    name="boleto_protesto_automatico"
                    checked={settings.boleto_protesto_automatico}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Habilitar protesto automatico em cartorio
                  </span>
                </div>
              </div>

              {/* Dias para Protesto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dias para Protesto Automatico
                </label>
                <input
                  type="number"
                  name="boleto_dias_protesto"
                  value={settings.boleto_dias_protesto}
                  onChange={handleInputChange}
                  min="3"
                  max="30"
                  step="1"
                  required
                  disabled={!settings.boleto_protesto_automatico}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {/* Aba 2 - Prazos e Regras */}
          {activeTab === 'prazos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Prazo Retirada Veiculo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prazo para Retirada do Veiculo (dias)
                </label>
                <input
                  type="number"
                  name="prazo_retirada_veiculo"
                  value={settings.prazo_retirada_veiculo}
                  onChange={handleInputChange}
                  min="1"
                  max="30"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Dias apos disponibilizacao antes de cobrar estacionamento
                </p>
              </div>

              {/* Prazo Emissao NF */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dia de Emissao da NF
                </label>
                <input
                  type="number"
                  name="prazo_emissao_nf"
                  value={settings.prazo_emissao_nf}
                  onChange={handleInputChange}
                  min="1"
                  max="28"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Dia do mes para emissao da Nota Fiscal
                </p>
              </div>

              {/* Prazo Negativacao */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prazo para Negativacao SPC/SERASA (dias)
                </label>
                <input
                  type="number"
                  name="prazo_negativacao"
                  value={settings.prazo_negativacao}
                  onChange={handleInputChange}
                  min="1"
                  max="90"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Dias de atraso para inclusao em orgaos de protecao ao credito
                </p>
              </div>

              {/* Prazo Envio Documentos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prazo para Envio de Documentos (dias uteis)
                </label>
                <input
                  type="number"
                  name="prazo_envio_documentos"
                  value={settings.prazo_envio_documentos}
                  onChange={handleInputChange}
                  min="1"
                  max="30"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Dias uteis para envio de documentos solicitados
                </p>
              </div>

              {/* Prazo Negociacao Reequilibrio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prazo para Negociacao de Reequilibrio (dias)
                </label>
                <input
                  type="number"
                  name="prazo_negociacao_reequilibrio"
                  value={settings.prazo_negociacao_reequilibrio}
                  onChange={handleInputChange}
                  min="1"
                  max="90"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Prazo para negociacao de reequilibrio economico-financeiro
                </p>
              </div>

              {/* Dias Suspensao Servico */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dias para Suspensao do Servico
                </label>
                <input
                  type="number"
                  name="dias_suspensao_servico"
                  value={settings.dias_suspensao_servico}
                  onChange={handleInputChange}
                  min="1"
                  max="30"
                  step="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Dias de atraso para suspensao imediata do servico
                </p>
              </div>

              {/* Indice de Reajuste */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Indice de Reajuste Preferencial
                </label>
                <select
                  name="indice_reajuste_preferencial"
                  value={settings.indice_reajuste_preferencial}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="MAIOR_INDICE">Maior Indice (IGPM, IPCA ou similar)</option>
                  <option value="IGPM">IGPM/FGV</option>
                  <option value="IPCA">IPCA/IBGE</option>
                </select>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Indice utilizado para reajuste anual dos contratos
                </p>
              </div>

              {/* Separador DPO */}
              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-6 mt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  DPO - Encarregado de Protecao de Dados (LGPD)
                </h3>
              </div>

              {/* Nome DPO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do DPO
                </label>
                <input
                  type="text"
                  name="dpo_name"
                  value={settings.dpo_name || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Email DPO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email do DPO
                </label>
                <input
                  type="email"
                  name="dpo_email"
                  value={settings.dpo_email || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Telefone DPO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefone do DPO
                </label>
                <input
                  type="tel"
                  name="dpo_phone"
                  value={settings.dpo_phone || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={!hasChanges || saving}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white font-medium transition-colors ${
                  hasChanges && !saving
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                <CheckCircleIcon className="w-5 h-5" />
                {saving ? 'Salvando...' : 'Salvar Configuracoes'}
              </button>

              {hasChanges && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                  Descartar
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ClockIcon className="w-5 h-5" />
              {showHistory ? 'Ocultar Historico' : 'Ver Historico'}
            </button>
          </div>
        </form>
      </div>

      {/* Preview Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">
          Exemplo de Aplicacao
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-blue-800 dark:text-blue-200">
              <strong>Contrato de R$ 10.000/mes por 6 meses:</strong>
            </p>
            <ul className="mt-2 space-y-1 text-blue-700 dark:text-blue-300">
              <li>Valor base: R$ 60.000,00</li>
              <li>TDA ({settings.percentual_tda}% sobre vincendos): R$ {(30000 * settings.percentual_tda / 100).toFixed(2)}</li>
              <li>Caucao ({settings.deposit_percentage_default}%): R$ {(10000 * settings.deposit_percentage_default / 100).toFixed(2)}</li>
            </ul>
          </div>
          <div>
            <p className="text-blue-800 dark:text-blue-200">
              <strong>Inadimplencia:</strong>
            </p>
            <ul className="mt-2 space-y-1 text-blue-700 dark:text-blue-300">
              <li>Multa atraso: {settings.percentual_multa_atraso}%</li>
              <li>Juros diario: {settings.percentual_juros_diario}%</li>
              <li>Negativacao apos: {settings.prazo_negativacao} dias</li>
              <li>Suspensao apos: {settings.dias_suspensao_servico} dias</li>
            </ul>
          </div>
        </div>
      </div>

      {/* History Table */}
      {showHistory && history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-gray-500" />
              Historico de Alteracoes
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">TCA (%)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">TDA (%)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Franquia KM</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">KM Extra</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Multa Atraso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Negativacao</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {history.slice(0, 10).map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatDate(item.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{Number(item.taxa_administrativa_percentual).toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{Number(item.percentual_tda || 30).toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{Number(item.franquia_km_mensal).toLocaleString('pt-BR')} km</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">R$ {Number(item.valor_km_excedente).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{Number(item.percentual_multa_atraso).toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{item.prazo_negativacao || 30} dias</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showHistory && history.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Nenhum historico de alteracoes encontrado
          </p>
        </div>
      )}
    </div>
  )
}

export default ContractSettingsPage
